import React, { useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import { CheckCircle } from '@phosphor-icons/react';

const workforceOptions = ['1-10', '11-50', '51-200', '201-500', '500+'];

const Partner = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    email: '',
    phone: '',
    location: '',
    business_line: '',
    company_name: '',
    website: '',
    workforce: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/inquiries/partner', formData);
      setSubmitted(true);
      toast.success('Partnership inquiry submitted!');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="partner-success" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4] flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md px-6">
          <CheckCircle size={64} weight="thin" className="text-green-600 mx-auto mb-4" />
          <h2 className="text-3xl font-light mb-4">Thank You!</h2>
          <p className="text-[#7a5e75] mb-8">
            We've received your partnership inquiry. Our team will reach out within 48 hours.
          </p>
          <button onClick={() => setSubmitted(false)} data-testid="submit-another-partner" className="btn-secondary">
            Submit Another Inquiry
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="partner-page" className="min-h-screen bg-[#F4F4F4]">
      {/* Hero Image */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/d1dcf077-4e7b-4264-9d73-3de5a1021d6b/images/f7b33120e7959c48903b5149ad0a7933d0c07e992ade21290c5f6822e0997b80.png"
          alt="Premium matte finish FRP planters in luxury hotel lobby"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50" />
        <div className="absolute bottom-0 left-0 right-0 p-12 max-w-7xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="partner-heading"
            className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-white"
          >
            Let's go hand in hand.
          </motion.h1>
        </div>
      </div>

      {/* Body Text */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <p className="text-base md:text-lg text-[#7a5e75] leading-relaxed mb-4">
            We are planter manufacturers from Coimbatore, India and our reach extends across the Globe.
          </p>
          <p className="text-base md:text-lg text-[#7a5e75] leading-relaxed">
            The fact is, alone we can sow one seed, but with you we can sow one thousand.
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 data-testid="partner-form-heading" className="text-2xl md:text-3xl font-light tracking-tight text-[#641b4d] mb-10">
            Let's sow together. Let's grow together.
          </h2>

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <input
                  type="text"
                  name="first_name"
                  data-testid="partner-first-name"
                  placeholder="First name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="input-underline w-full"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  data-testid="partner-email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-underline w-full"
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  data-testid="partner-phone"
                  placeholder="Contact number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="input-underline w-full"
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <input
                  type="text"
                  name="location"
                  data-testid="partner-location"
                  placeholder="Location"
                  value={formData.location}
                  onChange={handleChange}
                  className="input-underline w-full"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="business_line"
                  data-testid="partner-business-line"
                  placeholder="Current line of business"
                  value={formData.business_line}
                  onChange={handleChange}
                  className="input-underline w-full"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="company_name"
                  data-testid="partner-company-name"
                  placeholder="Company name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="input-underline w-full"
                />
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <input
                  type="text"
                  name="website"
                  data-testid="partner-website"
                  placeholder="Website"
                  value={formData.website}
                  onChange={handleChange}
                  className="input-underline w-full"
                />
              </div>
              <div>
                <select
                  name="workforce"
                  data-testid="partner-workforce"
                  value={formData.workforce}
                  onChange={handleChange}
                  className="input-underline w-full bg-transparent text-[#7a5e75] appearance-none cursor-pointer"
                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234A5D54' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0 center' }}
                >
                  <option value="" disabled>Your Current workforce</option>
                  {workforceOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <input
                  type="text"
                  name="message"
                  data-testid="partner-message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  className="input-underline w-full"
                />
              </div>
            </div>

            <div className="text-center pt-4">
              <button
                type="submit"
                data-testid="partner-submit-button"
                disabled={loading}
                className="text-[#641b4d] text-base font-semibold border-b-2 border-[#641b4d] pb-1 hover:text-[#bc64aa] hover:border-[#bc64aa] transition-colors disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default Partner;
